import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, index, real } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

import { tenants, companies, areas, sites, users, gates } from './common';

export const workers = pgTable("workers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  role: text("role"),
  /** @deprecated Denormalized display-only. Use companyId for queries/joins. Removal-by: v2 authority model migration (Task #1731). */
  company: text("company"),
  companyId: text("company_id").references(() => companies.id),
  email: text("email"),
  phone: text("phone"),
  /** @deprecated Denormalized display-only. Use primaryAreaId for queries/joins. Removal-by: v2 authority model migration (Task #1731). */
  area: text("area"),
  primaryAreaId: text("primary_area_id").references(() => areas.id),
  status: text("status").default("active"),
  accessStatus: text("access_status").default("active"),
  joinedDate: text("joined_date"),
  certifications: jsonb("certifications").$type<{
    name: string;
    expiry: string;
    status: string;
    fileId?: string;
    type?: string;
  }[]>().default([]),
  history: jsonb("history").$type<{
    date: string;
    action: string;
    detail: string;
  }[]>().default([]),
  attendance: jsonb("attendance").$type<{
    id: string;
    time: string;
    direction: string;
    gate: string;
    device: string;
    operator: string;
    decision: string;
    reason?: string;
  }[]>().default([]),

  preferredName: text("preferred_name"),
  displayName: text("display_name"),
  nationality: text("nationality"),
  dateOfBirth: text("date_of_birth"),
  gender: text("gender"),
  governmentIdRef: text("government_id_ref"),
  idType: text("id_type"),
  idExpiry: text("id_expiry"),
  biometricRef: text("biometric_ref"),
  identityVerificationStatus: text("identity_verification_status").default("unverified"),

  employerCompanyId: text("employer_company_id").references(() => companies.id),
  sponsorCompanyId: text("sponsor_company_id").references(() => companies.id),
  department: text("department"),
  departmentId: text("department_id"),
  divisionId: text("division_id"),
  tradeProfession: text("trade_profession"),
  jobTitle: text("job_title"),
  workerCategory: text("worker_category").default("direct"),
  siteAssignments: text("site_assignments").array().default([]),
  areaAssignments: text("area_assignments").array().default([]),
  projectAssignments: text("project_assignments").array().default([]),
  badgeNumber: text("badge_number"),
  crewTeam: text("crew_team"),
  supervisor: text("supervisor"),
  roleBundles: text("role_bundles").array().default([]),
  riskClass: text("risk_class"),
  accessProfile: text("access_profile"),
  inductionStatus: text("induction_status").default("pending"),
  competencySummary: jsonb("competency_summary").$type<Record<string, any>>().default({}),
  credentialSummaryExpired: integer("credential_summary_expired").default(0),
  credentialSummarySuspended: integer("credential_summary_suspended").default(0),
  blockedForAccess: boolean("blocked_for_access").default(false),
  blockedForPtw: boolean("blocked_for_ptw").default(false),
  walletId: text("wallet_id"),
  identityProfileLink: text("identity_profile_link"),
  documentSetRef: text("document_set_ref"),
  lifecycleStatus: text("lifecycle_status").default("draft"),
  photoUrl: text("photo_url"),
  signatureUrl: text("signature_url"),
  gdprConsentGiven: boolean("gdpr_consent_given").default(false),
  gdprConsentAt: timestamp("gdpr_consent_at", { withTimezone: true }),
  printedQrToken: text("printed_qr_token").unique(),
  printedQrGeneratedAt: timestamp("printed_qr_generated_at", { withTimezone: true }),
  emergencyContactName: text("emergency_contact_name"),
  emergencyContactPhone: text("emergency_contact_phone"),
  medicalNotes: text("medical_notes"),
  nameArabic: varchar("name_arabic"),
}, (t) => ({
  idxWorkersTenantAccessStatus: index("idx_workers_tenant_access_status").on(t.tenantId, t.accessStatus),
  idxWorkersTenantLifecycleStatus: index("idx_workers_tenant_lifecycle_status").on(t.tenantId, t.lifecycleStatus),
  idxWorkersTenantCompanyId: index("idx_workers_tenant_company_id").on(t.tenantId, t.companyId),
  idxWorkersTenantEmployerCompanyId: index("idx_workers_tenant_employer_company_id").on(t.tenantId, t.employerCompanyId),
  idxWorkersTenantStatus: index("idx_workers_tenant_status").on(t.tenantId, t.status),
  idxWorkersEmailTenant: index("idx_workers_email_tenant").on(t.email, t.tenantId),
  idxWorkersGovernmentIdRefTenant: index("idx_workers_government_id_ref_tenant").on(t.tenantId, t.governmentIdRef),
}));
export const insertWorkerSchema = createInsertSchema(workers).omit({ tenantId: true, siteAssignments: true, areaAssignments: true });
export type InsertWorker = z.infer<typeof insertWorkerSchema>;
export type Worker = typeof workers.$inferSelect;

export const workerUserLinks = pgTable("worker_user_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id, { onDelete: 'cascade' }),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  linkType: text("link_type").notNull().default("primary"),
  status: text("status").notNull().default("active"),
  reason: text("reason"),
  linkedBy: text("linked_by"),
  linkedByRole: text("linked_by_role"),
  unlinkedBy: text("unlinked_by"),
  unlinkedAt: text("unlinked_at"),
  unlinkedReason: text("unlinked_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertWorkerUserLinkSchema = createInsertSchema(workerUserLinks).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertWorkerUserLink = z.infer<typeof insertWorkerUserLinkSchema>;
export type WorkerUserLink = typeof workerUserLinks.$inferSelect;

// Canonical company type values — used for validation in insertCompanySchema and shared across frontend/backend
export const workerAreaAssignments = pgTable("worker_area_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  areaId: text("area_id").notNull().references(() => areas.id),
  assignmentType: text("assignment_type").notNull().default("permanent"),
  companyId: text("company_id"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  status: text("status").notNull().default("active"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertWorkerAreaAssignmentSchema = createInsertSchema(workerAreaAssignments).omit({ tenantId: true });
export type InsertWorkerAreaAssignment = z.infer<typeof insertWorkerAreaAssignmentSchema>;
export type WorkerAreaAssignment = typeof workerAreaAssignments.$inferSelect;
export const workerCredentials = pgTable("worker_credentials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  workerName: text("worker_name").notNull(),
  credentialTemplateId: text("credential_template_id").notNull(),
  credentialName: text("credential_name").notNull(),
  areaId: text("area_id").notNull(),
  issueDate: text("issue_date"),
  expiryDate: text("expiry_date"),
  daysRemaining: integer("days_remaining").notNull().default(0),
  expiryStatus: text("expiry_status").notNull().default("Valid"),
  autoSuspendOnExpiry: boolean("auto_suspend_on_expiry").notNull().default(false),
  renewalRequested: boolean("renewal_requested").notNull().default(false),
  renewalInProgress: boolean("renewal_in_progress").notNull().default(false),
  renewalHistory: text("renewal_history").array().default([]),
  suspensionApplied: boolean("suspension_applied").notNull().default(false),
  lastNotificationDate: text("last_notification_date"),
  escalationTriggered: boolean("escalation_triggered").notNull().default(false),
  complianceScore: integer("compliance_score").notNull().default(100),
}, (t) => ({
  idxWorkerCredentialsTenantId: index("idx_worker_credentials_tenant_id").on(t.tenantId),
  idxWorkerCredentialsWorkerId: index("idx_worker_credentials_worker_id").on(t.workerId),
  idxWorkerCredentialsTenantWorkerId: index("idx_worker_credentials_tenant_worker_id").on(t.tenantId, t.workerId),
}));
export const insertWorkerCredentialSchema = createInsertSchema(workerCredentials).omit({ tenantId: true });
export type InsertWorkerCredential = z.infer<typeof insertWorkerCredentialSchema>;
export type WorkerCredential = typeof workerCredentials.$inferSelect;
export const musterEvents = pgTable("muster_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  triggeredBy: text("triggered_by").notNull(),
  triggerTime: text("trigger_time"),
  triggerReason: text("trigger_reason").notNull(),
  linkedIncidentId: text("linked_incident_id"),
  areasAffected: text("areas_affected").array().default([]),
  evacuationStatus: text("evacuation_status").notNull().default("Active"),
  totalWorkersOnSite: integer("total_workers_on_site").notNull().default(0),
  totalEvacuated: integer("total_evacuated").notNull().default(0),
  totalMissing: integer("total_missing").notNull().default(0),
  totalInjured: integer("total_injured").notNull().default(0),
  lockdownApplied: boolean("lockdown_applied").notNull().default(false),
  emergencyServicesNotified: boolean("emergency_services_notified").notNull().default(false),
  auditLog: text("audit_log").array().default([]),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertMusterEventSchema = createInsertSchema(musterEvents).omit({ tenantId: true });
export type InsertMusterEvent = z.infer<typeof insertMusterEventSchema>;
export type MusterEvent = typeof musterEvents.$inferSelect;
export const workerLifecycleStates = pgTable("worker_lifecycle_states", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  scopeType: text("scope_type").notNull(),
  scopeId: text("scope_id").notNull(),
  lifecycleState: text("lifecycle_state").notNull().default("not_assigned"),
  previousState: text("previous_state"),
  effectiveDate: text("effective_date"),
  reason: text("reason"),
  changedBy: text("changed_by"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertWorkerLifecycleStateSchema = createInsertSchema(workerLifecycleStates).omit({ id: true, tenantId: true });
export type InsertWorkerLifecycleState = z.infer<typeof insertWorkerLifecycleStateSchema>;
export type WorkerLifecycleState = typeof workerLifecycleStates.$inferSelect;
export const workerLifecycleTransitions = pgTable("worker_lifecycle_transitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  transitionType: text("transition_type").notNull(),
  scopeType: text("scope_type"),
  scopeId: text("scope_id"),
  fromState: text("from_state"),
  toState: text("to_state").notNull(),
  reasonCode: text("reason_code"),
  approvalRequired: boolean("approval_required").notNull().default(false),
  approvedBy: text("approved_by"),
  effectsOnAccess: jsonb("effects_on_access").$type<Record<string, any>>().default({}),
  effectsOnPtw: jsonb("effects_on_ptw").$type<Record<string, any>>().default({}),
  effectsOnWallet: jsonb("effects_on_wallet").$type<Record<string, any>>().default({}),
  restrictions: jsonb("restrictions").$type<Record<string, any>>().default({}),
  evidence: jsonb("evidence").$type<Record<string, any>>().default({}),
  comments: text("comments"),
  triggeredBy: text("triggered_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertWorkerLifecycleTransitionSchema = createInsertSchema(workerLifecycleTransitions).omit({ id: true, tenantId: true });
export type InsertWorkerLifecycleTransition = z.infer<typeof insertWorkerLifecycleTransitionSchema>;
export type WorkerLifecycleTransition = typeof workerLifecycleTransitions.$inferSelect;
export const deployments = pgTable("deployments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  deploymentType: text("deployment_type").notNull().default("project"),
  companyId: text("company_id").notNull(),
  hostCompanyId: text("host_company_id"),
  projectId: text("project_id").notNull(),
  siteId: text("site_id").notNull(),
  areaId: text("area_id"),
  crewTeamId: text("crew_team_id"),
  supervisorId: text("supervisor_id"),
  shiftId: text("shift_id"),
  workPackageId: text("work_package_id"),
  roleContext: text("role_context"),
  workerOperationalRole: text("worker_operational_role"),
  deploymentStartDate: text("deployment_start_date"),
  deploymentEndDate: text("deployment_end_date"),
  activationDate: text("activation_date"),
  standbyFlag: boolean("standby_flag").notNull().default(false),
  temporaryFlag: boolean("temporary_flag").notNull().default(false),
  recurringFlag: boolean("recurring_flag").notNull().default(false),
  status: text("status").notNull().default("planned"),
  readinessSnapshotAtAssignment: jsonb("readiness_snapshot_at_assignment").$type<Record<string, any>>().default({}),
  requiredBundleIds: text("required_bundle_ids").array().default([]),
  deploymentBlockers: jsonb("deployment_blockers").$type<Record<string, any>[]>().default([]),
  conditionalRestrictions: jsonb("conditional_restrictions").$type<Record<string, any>[]>().default([]),
  createdBy: text("created_by"),
  approvedBy: text("approved_by"),
  suspendedBy: text("suspended_by"),
  endedBy: text("ended_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertDeploymentSchema = createInsertSchema(deployments).omit({ id: true, tenantId: true });
export type InsertDeployment = z.infer<typeof insertDeploymentSchema>;
export type Deployment = typeof deployments.$inferSelect;
export const transferRequests = pgTable("transfer_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  transferType: text("transfer_type").notNull(),
  sourceCompanyId: text("source_company_id"),
  sourceProjectId: text("source_project_id"),
  sourceSiteId: text("source_site_id"),
  sourceAreaId: text("source_area_id"),
  sourceCrewId: text("source_crew_id"),
  targetCompanyId: text("target_company_id"),
  targetProjectId: text("target_project_id"),
  targetSiteId: text("target_site_id"),
  targetAreaId: text("target_area_id"),
  targetCrewId: text("target_crew_id"),
  effectiveDate: text("effective_date"),
  reasonCode: text("reason_code"),
  readinessRecheckRequired: boolean("readiness_recheck_required").notNull().default(false),
  requiredApprovals: jsonb("required_approvals").$type<Record<string, any>>().default({}),
  status: text("status").notNull().default("requested"),
  handoverNotes: text("handover_notes"),
  linkedLifecycleTransitions: text("linked_lifecycle_transitions").array().default([]),
  sourceDeploymentId: text("source_deployment_id"),
  targetDeploymentId: text("target_deployment_id"),
  createdBy: text("created_by"),
  approvedBy: text("approved_by"),
  executedBy: text("executed_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTransferRequestSchema = createInsertSchema(transferRequests).omit({ id: true, tenantId: true });
export type InsertTransferRequest = z.infer<typeof insertTransferRequestSchema>;
export type TransferRequest = typeof transferRequests.$inferSelect;
export const workerSuspensions = pgTable("worker_suspensions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  scopeType: text("scope_type").notNull().default("global"),
  scopeId: text("scope_id"),
  reasonCode: text("reason_code").notNull(),
  reasonDetail: text("reason_detail"),
  startDate: text("start_date").notNull(),
  expectedEndDate: text("expected_end_date"),
  actualResolutionDate: text("actual_resolution_date"),
  imposedBy: text("imposed_by"),
  reviewRequiredBy: text("review_required_by"),
  status: text("status").notNull().default("active"),
  suspensionLevel: text("suspension_level").notNull().default("hard"),
  readmissionConditions: jsonb("readmission_conditions").$type<Record<string, any>[]>().default([]),
  returnToServiceChecklist: jsonb("return_to_service_checklist").$type<Record<string, any>>().default({}),
  effectOnDeployments: jsonb("effect_on_deployments").$type<Record<string, any>>().default({}),
  effectOnAccess: jsonb("effect_on_access").$type<Record<string, any>>().default({}),
  effectOnPtw: jsonb("effect_on_ptw").$type<Record<string, any>>().default({}),
  linkedEvidenceRef: text("linked_evidence_ref"),
  linkedIncidentRef: text("linked_incident_ref"),
  comments: text("comments"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertWorkerSuspensionSchema = createInsertSchema(workerSuspensions).omit({ id: true, tenantId: true });
export type InsertWorkerSuspension = z.infer<typeof insertWorkerSuspensionSchema>;
export type WorkerSuspension = typeof workerSuspensions.$inferSelect;
export const demobilizationRecords = pgTable("demobilization_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  scopeType: text("scope_type").notNull(),
  sourceDeploymentId: text("source_deployment_id"),
  reasonCode: text("reason_code").notNull(),
  requestedDate: text("requested_date"),
  effectiveDate: text("effective_date"),
  checklistState: jsonb("checklist_state").$type<Record<string, any>>().default({}),
  returnEquipmentCheck: boolean("return_equipment_check").notNull().default(false),
  gateAccessDeactivationFlag: boolean("gate_access_deactivation_flag").notNull().default(false),
  areaAccessRemovalState: text("area_access_removal_state"),
  ptwInvolvementCheck: boolean("ptw_involvement_check").notNull().default(false),
  supervisorAcknowledgement: boolean("supervisor_acknowledgement").notNull().default(false),
  companyAcknowledgement: boolean("company_acknowledgement").notNull().default(false),
  status: text("status").notNull().default("pending"),
  futureRemobilizationEligible: boolean("future_remobilization_eligible").notNull().default(true),
  createdBy: text("created_by"),
  completedBy: text("completed_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertDemobilizationRecordSchema = createInsertSchema(demobilizationRecords).omit({ id: true, tenantId: true });
export type InsertDemobilizationRecord = z.infer<typeof insertDemobilizationRecordSchema>;
export type DemobilizationRecord = typeof demobilizationRecords.$inferSelect;
export const offboardingRecords = pgTable("offboarding_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  offboardingType: text("offboarding_type").notNull(),
  reasonCode: text("reason_code").notNull(),
  effectiveDate: text("effective_date"),
  checklistState: jsonb("checklist_state").$type<Record<string, any>>().default({}),
  companyUnlinkRequired: boolean("company_unlink_required").notNull().default(false),
  walletStateAction: text("wallet_state_action"),
  credentialArchivalPolicy: text("credential_archival_policy"),
  userAccountUnlinkFlag: boolean("user_account_unlink_flag").notNull().default(false),
  dataRetentionPolicy: text("data_retention_policy"),
  status: text("status").notNull().default("pending"),
  approvedBy: text("approved_by"),
  notes: text("notes"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertOffboardingRecordSchema = createInsertSchema(offboardingRecords).omit({ id: true, tenantId: true });
export type InsertOffboardingRecord = z.infer<typeof insertOffboardingRecordSchema>;
export type OffboardingRecord = typeof offboardingRecords.$inferSelect;
export const workerMovementAuditEvents = pgTable("worker_movement_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  eventType: text("event_type").notNull(),
  actor: text("actor"),
  actorRole: text("actor_role"),
  affectedScope: jsonb("affected_scope").$type<Record<string, any>>().default({}),
  oldState: text("old_state"),
  newState: text("new_state"),
  reasonCode: text("reason_code"),
  linkedEvidenceRef: text("linked_evidence_ref"),
  comments: text("comments"),
  policyVersionUsed: text("policy_version_used"),
  batchId: text("batch_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxWorkerMovementAuditTenantWorker: index("idx_worker_movement_audit_tenant_worker").on(t.tenantId, t.workerId),
  idxWorkerMovementAuditTenantCreated: index("idx_worker_movement_audit_tenant_created").on(t.tenantId, t.createdAt),
}));
export const insertWorkerMovementAuditEventSchema = createInsertSchema(workerMovementAuditEvents).omit({ id: true, tenantId: true });
export type InsertWorkerMovementAuditEvent = z.infer<typeof insertWorkerMovementAuditEventSchema>;
export type WorkerMovementAuditEvent = typeof workerMovementAuditEvents.$inferSelect;

// ─── Shift Definitions ──────────────────────────────────────────────
export const workerPresenceStates = pgTable("worker_presence_states", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().unique().references(() => workers.id),
  currentState: text("current_state").notNull().default("off_site"),
  currentSiteId: text("current_site_id").references(() => sites.id),
  currentAreaId: text("current_area_id").references(() => areas.id),
  lastEventId: text("last_event_id"),
  lastEventTime: text("last_event_time"),
  lastGateId: text("last_gate_id"),
  stateChangedAt: text("state_changed_at"),
  onSiteSince: text("on_site_since"),
  companyId: text("company_id"),
  supervisorId: text("supervisor_id"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxWorkerPresenceStatesTenantWorker: index("idx_worker_presence_states_tenant_worker").on(t.tenantId, t.workerId),
  idxWorkerPresenceStatesTenantCurrentState: index("idx_worker_presence_states_tenant_current_state").on(t.tenantId, t.currentState),
}));
export const insertWorkerPresenceStateSchema = createInsertSchema(workerPresenceStates).omit({ id: true, tenantId: true });
export type InsertWorkerPresenceState = z.infer<typeof insertWorkerPresenceStateSchema>;
export type WorkerPresenceState = typeof workerPresenceStates.$inferSelect;
export const musterSessions = pgTable("muster_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  musterEventId: text("muster_event_id").references(() => musterEvents.id),
  siteId: text("site_id").notNull().references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),
  gateId: text("gate_id").references(() => gates.id),
  initiatedBy: text("initiated_by").notNull(),
  initiatedAt: text("initiated_at").notNull(),
  reason: text("reason").notNull().default("emergency"),
  assemblyPoint: text("assembly_point"),
  status: text("status").notNull().default("active"),
  totalWorkers: integer("total_workers").notNull().default(0),
  accountedWorkers: integer("accounted_workers").notNull().default(0),
  unaccountedWorkers: integer("unaccounted_workers").notNull().default(0),
  closedAt: text("closed_at"),
  closedBy: text("closed_by"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertMusterSessionSchema = createInsertSchema(musterSessions).omit({ id: true, tenantId: true });
export type InsertMusterSession = z.infer<typeof insertMusterSessionSchema>;
export type MusterSession = typeof musterSessions.$inferSelect;
export const musterResponses = pgTable("muster_responses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  musterSessionId: text("muster_session_id").notNull().references(() => musterSessions.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  workerName: text("worker_name"),
  companyId: text("company_id").references(() => companies.id),
  supervisorId: text("supervisor_id"),
  lastKnownArea: text("last_known_area"),
  gpsCoordinates: text("gps_coordinates"),
  accountedStatus: text("accounted_status").notNull().default("unaccounted"),
  confirmedBy: text("confirmed_by"),
  confirmedAt: text("confirmed_at"),
  confirmationMethod: text("confirmation_method"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertMusterResponseSchema = createInsertSchema(musterResponses).omit({ id: true, tenantId: true });
export type InsertMusterResponse = z.infer<typeof insertMusterResponseSchema>;
export type MusterResponse = typeof musterResponses.$inferSelect;

// ─── Inspection & Maintenance Audit Log ──────────────────────────────
export const toolboxTalks = pgTable("toolbox_talks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  supervisorId: text("supervisor_id"),
  supervisorName: text("supervisor_name"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  topic: text("topic").notNull(),
  hazardCategories: text("hazard_categories").array().default([]),
  attendees: text("attendees").array().default([]),
  acknowledged: boolean("acknowledged").notNull().default(false),
  conductedAt: timestamp("conducted_at", { withTimezone: true }).defaultNow(),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertToolboxTalkSchema = createInsertSchema(toolboxTalks).omit({ id: true, tenantId: true, createdAt: true }).extend({
  conductedAt: z.union([z.date(), z.string().datetime()]).transform(v => typeof v === "string" ? new Date(v) : v).optional(),
});
export type InsertToolboxTalk = z.infer<typeof insertToolboxTalkSchema>;
export type ToolboxTalk = typeof toolboxTalks.$inferSelect;

// ─── Toolbox Talk Attendance Roster ──────────────────────────────────
export const toolboxTalkAttendance = pgTable("toolbox_talk_attendance", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  toolboxTalkId: text("toolbox_talk_id").notNull(),
  workerId: text("worker_id").notNull(),
  workerName: text("worker_name"),
  attended: boolean("attended").notNull().default(false),
  recordedAt: timestamp("recorded_at", { withTimezone: true }).defaultNow(),
  recordedBy: text("recorded_by"),
});
export const insertToolboxTalkAttendanceSchema = createInsertSchema(toolboxTalkAttendance).omit({ id: true, tenantId: true });
export type InsertToolboxTalkAttendance = z.infer<typeof insertToolboxTalkAttendanceSchema>;
export type ToolboxTalkAttendance = typeof toolboxTalkAttendance.$inferSelect;

// ─── Shift Handovers ─────────────────────────────────────────────────
export const shiftHandovers = pgTable("shift_handovers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  supervisorId: text("supervisor_id"),
  supervisorName: text("supervisor_name"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  notes: text("notes").notNull(),
  openExceptionCount: integer("open_exception_count").notNull().default(0),
  activePermitIds: text("active_permit_ids").array().default([]),
  followUpRequired: boolean("follow_up_required").notNull().default(false),
  followUpItems: text("follow_up_items"),
  handoverTime: timestamp("handover_time", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertShiftHandoverSchema = createInsertSchema(shiftHandovers).omit({ id: true, tenantId: true, createdAt: true }).extend({
  handoverTime: z.union([z.date(), z.string().datetime()]).transform(v => typeof v === "string" ? new Date(v) : v).optional(),
});
export type InsertShiftHandover = z.infer<typeof insertShiftHandoverSchema>;
export type ShiftHandover = typeof shiftHandovers.$inferSelect;

// ─── Handover Checklist Items ─────────────────────────────────────────
export const handoverChecklistItems = pgTable("handover_checklist_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  handoverId: text("handover_id").notNull().references(() => shiftHandovers.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  body: text("body"),
  done: boolean("done").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxHandoverChecklistItemsTenantId: index("idx_handover_checklist_items_tenant_id").on(t.tenantId),
  idxHandoverChecklistItemsHandoverId: index("idx_handover_checklist_items_handover_id").on(t.handoverId),
  idxHandoverChecklistItemsTenantHandover: index("idx_handover_checklist_items_tenant_handover").on(t.tenantId, t.handoverId),
}));
export const insertHandoverChecklistItemSchema = createInsertSchema(handoverChecklistItems).omit({ id: true, tenantId: true, createdAt: true });
export type InsertHandoverChecklistItem = z.infer<typeof insertHandoverChecklistItemSchema>;
export type HandoverChecklistItem = typeof handoverChecklistItems.$inferSelect;

// ─── Worker Erasure Log (GDPR Article 17 Right to Erasure) ───────────
export const workerErasureLogs = pgTable("worker_erasure_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  requestedByUserId: text("requested_by_user_id"),
  erasedAt: timestamp("erased_at", { withTimezone: true }).defaultNow(),
  tablesCleared: text("tables_cleared").array().notNull().default([]),
  verificationPassed: boolean("verification_passed").notNull().default(false),
  notes: text("notes"),
  auditToken: text("audit_token"),
});
export const insertWorkerErasureLogSchema = createInsertSchema(workerErasureLogs).omit({ id: true, erasedAt: true });
export type InsertWorkerErasureLog = z.infer<typeof insertWorkerErasureLogSchema>;
export type WorkerErasureLog = typeof workerErasureLogs.$inferSelect;

// ─── Workforce v2 Phase 7 — Identity as a First-Class Domain ──────────
// Task #1638. Promotes identity from inline columns on `workers` to its own
// canonical domain with documents, verifications, disputes, and history.
//
// All four tables are tenant-scoped. They are written by `server/engines/identity-engine.ts`
// and read by readiness, wallet, super-admin anomalies, and partner verify flows.

// Captured ID documents (passport, national ID, residence permit, driver licence, etc.).
// One row per uploaded document; the most-recent active row per (worker, docType) is the
// authoritative source for that document type.
export const workerIdentityDocuments = pgTable("worker_identity_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id, { onDelete: "cascade" }),
  // passport | national_id | residence_permit | driver_licence | other
  docType: text("doc_type").notNull(),
  docNumber: text("doc_number"),
  issuingCountry: text("issuing_country"),
  issuedAt: text("issued_at"),
  expiryDate: text("expiry_date"),
  // active | superseded | revoked | rejected
  status: text("status").notNull().default("active"),
  fileObjectKey: text("file_object_key"),
  capturedByUserId: text("captured_by_user_id"),
  // worker_self | tenant_admin | partner | super_admin | api
  capturedBy: text("captured_by").notNull().default("worker_self"),
  parsedFields: jsonb("parsed_fields").$type<Record<string, any>>().default({}),
  // Stub face/liveness scores; null until a real provider is wired in Phase 11.
  faceMatchScore: real("face_match_score"),
  livenessScore: real("liveness_score"),
  capturedAt: timestamp("captured_at", { withTimezone: true }).defaultNow(),
  supersededAt: timestamp("superseded_at", { withTimezone: true }),
  supersededBy: text("superseded_by_id"),
}, (t) => ({
  idxWidTenantWorker: index("idx_wid_tenant_worker").on(t.tenantId, t.workerId),
  idxWidWorkerStatus: index("idx_wid_worker_status").on(t.workerId, t.status),
  idxWidExpiry: index("idx_wid_expiry").on(t.tenantId, t.expiryDate),
}));
export const insertWorkerIdentityDocumentSchema = createInsertSchema(workerIdentityDocuments).omit({
  id: true, capturedAt: true, supersededAt: true, supersededBy: true,
});
export type InsertWorkerIdentityDocument = z.infer<typeof insertWorkerIdentityDocumentSchema>;
export type WorkerIdentityDocument = typeof workerIdentityDocuments.$inferSelect;

// Identity verifications — every check performed against a captured document.
// Either tenant-side (super_admin / tenant_admin / hse_officer), partner-side (mirrors
// the legacy `identity_verifications` row), or self-attestation.
export const workerIdentityVerifications = pgTable("worker_identity_verifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id, { onDelete: "cascade" }),
  documentId: text("document_id").references(() => workerIdentityDocuments.id, { onDelete: "set null" }),
  // verified | rejected | pending_review | revoked
  outcome: text("outcome").notNull().default("pending_review"),
  // manual | document_check | biometric | third_party | self_attested
  method: text("method").notNull().default("manual"),
  // tenant | partner | super_admin | worker_self
  verifierKind: text("verifier_kind").notNull().default("tenant"),
  verifierUserId: text("verifier_user_id"),
  verifierCompanyId: text("verifier_company_id"),
  // Cross-link to the legacy `identity_verifications` row when this verification
  // was created from the partner verify flow. Allows reconciliation during the
  // Phase 11 reverify saga without rewriting historical data.
  partnerVerificationId: text("partner_verification_id"),
  notes: text("notes"),
  evidence: jsonb("evidence").$type<Record<string, any>>().default({}),
  evidenceObjectKeys: text("evidence_object_keys").array().default([]),
  performedAt: timestamp("performed_at", { withTimezone: true }).defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
}, (t) => ({
  idxWivTenantWorker: index("idx_wiv_tenant_worker").on(t.tenantId, t.workerId),
  idxWivOutcome: index("idx_wiv_tenant_outcome").on(t.tenantId, t.outcome),
  idxWivPartnerLink: index("idx_wiv_partner_link").on(t.partnerVerificationId),
}));
export const insertWorkerIdentityVerificationSchema = createInsertSchema(workerIdentityVerifications).omit({
  id: true, performedAt: true,
});
export type InsertWorkerIdentityVerification = z.infer<typeof insertWorkerIdentityVerificationSchema>;
export type WorkerIdentityVerification = typeof workerIdentityVerifications.$inferSelect;

// Identity disputes — opened by a worker (or admin on their behalf) to challenge a
// verification outcome. Closed via signed evidence by a tenant admin / super admin.
export const workerIdentityDisputes = pgTable("worker_identity_disputes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id, { onDelete: "cascade" }),
  verificationId: text("verification_id").references(() => workerIdentityVerifications.id, { onDelete: "set null" }),
  documentId: text("document_id").references(() => workerIdentityDocuments.id, { onDelete: "set null" }),
  // open | under_review | upheld | rejected | withdrawn
  status: text("status").notNull().default("open"),
  reasonCode: text("reason_code").notNull().default("worker_disagrees"),
  reasonText: text("reason_text"),
  openedByUserId: text("opened_by_user_id"),
  // worker_self | tenant_admin | super_admin
  openedByKind: text("opened_by_kind").notNull().default("worker_self"),
  resolutionNotes: text("resolution_notes"),
  resolutionEvidence: jsonb("resolution_evidence").$type<Record<string, any>>().default({}),
  resolutionEvidenceObjectKeys: text("resolution_evidence_object_keys").array().default([]),
  resolutionSignatureRef: text("resolution_signature_ref"),
  resolvedByUserId: text("resolved_by_user_id"),
  openedAt: timestamp("opened_at", { withTimezone: true }).defaultNow(),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
}, (t) => ({
  idxWidisTenantWorker: index("idx_widis_tenant_worker").on(t.tenantId, t.workerId),
  idxWidisStatus: index("idx_widis_tenant_status").on(t.tenantId, t.status),
}));
export const insertWorkerIdentityDisputeSchema = createInsertSchema(workerIdentityDisputes).omit({
  id: true, openedAt: true, resolvedAt: true, resolvedByUserId: true,
});
export type InsertWorkerIdentityDispute = z.infer<typeof insertWorkerIdentityDisputeSchema>;
export type WorkerIdentityDispute = typeof workerIdentityDisputes.$inferSelect;

// Identity history — a flat append-only event stream for every identity-domain
// change. Replaces #691's bespoke history JSON on workers.
//
// Includes disclosure receipts (eventType = "disclosure"): every public scan
// creates a row visible in `Wallet > Activity > Disclosures` (Step 5).
export const workerIdentityHistory = pgTable("worker_identity_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id, { onDelete: "cascade" }),
  // document_captured | document_superseded | document_revoked | verification_recorded |
  // dispute_opened | dispute_resolved | trust_state_changed | disclosure
  eventType: text("event_type").notNull(),
  // Free-form sub-classification (e.g. "verified", "rejected", "expired", "pending").
  eventSubtype: text("event_subtype"),
  documentId: text("document_id"),
  verificationId: text("verification_id"),
  disputeId: text("dispute_id"),
  // Diff payload: { before: {...}, after: {...} } for document changes,
  // or scanner/disclosure context for disclosure rows.
  diff: jsonb("diff").$type<Record<string, any>>().default({}),
  // Free-form actor context — userId/role/ip — preserved even when the user record is later erased.
  actor: jsonb("actor").$type<{
    kind: "worker_self" | "tenant_admin" | "super_admin" | "partner" | "system" | "anonymous_scanner";
    userId?: string | null;
    name?: string | null;
    companyId?: string | null;
    opaqueScannerId?: string | null;
    ip?: string | null;
  }>().default({ kind: "system" }),
  // Disclosure receipt: which fields were exposed to the scanner.
  disclosedFields: text("disclosed_fields").array().default([]),
  // Disclosure receipt: optional scanner-shared geo.
  disclosedAtLocation: jsonb("disclosed_at_location").$type<{ lat?: number; lng?: number; label?: string } | null>(),
  occurredAt: timestamp("occurred_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxWihTenantWorker: index("idx_wih_tenant_worker").on(t.tenantId, t.workerId),
  idxWihTenantOccurred: index("idx_wih_tenant_occurred").on(t.tenantId, t.occurredAt),
  idxWihEventType: index("idx_wih_event_type").on(t.tenantId, t.eventType),
}));
export const insertWorkerIdentityHistorySchema = createInsertSchema(workerIdentityHistory).omit({
  id: true, occurredAt: true,
});
export type InsertWorkerIdentityHistory = z.infer<typeof insertWorkerIdentityHistorySchema>;
export type WorkerIdentityHistory = typeof workerIdentityHistory.$inferSelect;

// ─── Leave & Absence ──────────────────────────────────────────────────
